import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { API_URL } from "../config";
import { useAuth } from "../context/AuthContext";
import { useIsMobile } from "../hooks/useIsMobile";
import { useAutoAlert } from "../hooks/useAutoAlert";
import Button from "../components/ui/Button";
import { Input } from "../components/ui/Input";
import Modal from "../components/ui/Modal";
import ActionsMenu from "../components/ui/ActionsMenu";
import Dropdown from "../components/ui/Dropdown";
import ResetPasswordModal from "../components/modals/ResetPasswordModal";
import RemoveFromLeagueModal from "../components/modals/RemoveFromLeagueModal";
import EditPicksModal from "../components/modals/EditPicksModal";
import AssignCommissionerModal from "../components/modals/AssignCommissionerModal";
import AddToLeagueModal from "../components/modals/AddToLeagueModal";
import AdminGameManagement from "./AdminGameManagement";
import {
  FiUsers,
  FiShield,
  FiSettings,
  FiPlus,
  FiTrash2,
  FiEdit,
  FiCheck,
  FiKey,
  FiUserMinus,
  FiMail,
} from "react-icons/fi";
import AdminTableWrapper from "../components/ui/AdminTableWrapper";
import { formatDateTime } from "../utils/dateUtils";

const AdminPanel = () => {
  const { user } = useAuth();
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState("users");

  // Initialize state from URL parameters
  useEffect(() => {
    const tabParam = searchParams.get("tab") || "users";
    setActiveTab(tabParam);
  }, [searchParams]);

  const [leagues, setLeagues] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // User management state
  const [allUsers, setAllUsers] = useState([]);

  // League creation state
  const [newLeague, setNewLeague] = useState({
    name: "",
    description: "",
    isPaidLeague: false,
  });

  // Modal state
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showLeagueModal, setShowLeagueModal] = useState(false);
  const [showCommissionerModal, setShowCommissionerModal] = useState(false);
  const [selectedUserForCommissioner, setSelectedUserForCommissioner] =
    useState(null);
  const [showResetPasswordModal, setShowResetPasswordModal] = useState(false);
  const [showRemoveFromLeagueModal, setShowRemoveFromLeagueModal] =
    useState(false);
  const [showEditPicksModal, setShowEditPicksModal] = useState(false);
  const [showAssignCommissionerModal, setShowAssignCommissionerModal] =
    useState(false);
  const [showAddToLeagueModal, setShowAddToLeagueModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  // Invite user state
  const [inviteData, setInviteData] = useState({
    firstName: "",
    lastName: "",
    email: "",
  });
  const [inviteStep, setInviteStep] = useState("email"); // 'email' or 'details'
  const [existingUser, setExistingUser] = useState(null);

  // Commissioner assignment state
  const [commissionerData, setCommissionerData] = useState({
    leagueId: "",
  });

  // Auto-dismiss alerts after 5 seconds
  useAutoAlert(error, setError);
  useAutoAlert(success, setSuccess);

  useEffect(() => {
    if (user?.isAdmin) {
      fetchUsers();
      fetchLeagues();
    }
  }, [user]);

  const fetchUsers = async () => {
    try {
      const token = localStorage.getItem("token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};

      const response = await axios.get(`${API_URL}/users`, { headers });
      const usersData = response.data;

      // Store all users with full data for role management
      setAllUsers(usersData);
    } catch (err) {
      const errorMessage =
        err.response?.data?.error?.message ||
        err.response?.data?.error ||
        err.message;
      setError("Failed to fetch users: " + errorMessage);
    }
  };

  const fetchLeagues = async () => {
    try {
      const token = localStorage.getItem("token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};

      const response = await axios.get(
        `${API_URL}/leagues?includeInactive=false`,
        { headers },
      );
      const leaguesData = response.data.leagues || response.data;

      // Get league member counts
      const leaguesWithCounts = await Promise.all(
        leaguesData.map(async (league) => {
          try {
            const membersResponse = await axios.get(
              `${API_URL}/leagues/${league.id}/members`,
              { headers },
            );
            const memberCount = membersResponse.data?.members?.length || 0;
            return {
              ...league,
              memberCount,
            };
          } catch (err) {
            return {
              ...league,
              memberCount: 0,
            };
          }
        }),
      );

      setLeagues(
        leaguesWithCounts.map((l) => ({
          value: l.id,
          label: l.name,
          memberCount: l.memberCount,
          description: l.description,
          commissionerName: l.commissionerName,
        })),
      );
    } catch (err) {
      setError(
        "Failed to fetch leagues: " +
          (err.response?.data?.error || err.message),
      );
    }
  };

  const handleCreateLeague = async () => {
    if (!newLeague.name.trim()) {
      setError("League name is required");
      return;
    }

    try {
      setLoading(true);
      const token = localStorage.getItem("token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};

      await axios.post(`${API_URL}/leagues`, newLeague, { headers });
      setSuccess("League created successfully");
      setNewLeague({ name: "", description: "", isPaidLeague: false });
      setShowLeagueModal(false);
      await fetchLeagues(); // Refresh leagues list
    } catch (err) {
      setError(err.response?.data?.error || "Failed to create league");
    } finally {
      setLoading(false);
    }
  };

  const handleCheckEmail = async () => {
    if (!inviteData.email.trim()) {
      setError("Email is required");
      return;
    }

    try {
      setLoading(true);
      setError("");
      const token = localStorage.getItem("token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};

      const response = await axios.post(
        `${API_URL}/users/check-email`,
        {
          email: inviteData.email,
        },
        { headers },
      );

      if (response.data.exists) {
        setExistingUser(response.data.user);
        setInviteStep("existing");
      } else {
        setInviteStep("details");
      }
    } catch (err) {
      const errorMessage =
        err.response?.data?.error?.message ||
        err.response?.data?.error ||
        "Failed to check email";
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleInviteUser = async () => {
    if (
      !inviteData.firstName.trim() ||
      !inviteData.lastName.trim() ||
      !inviteData.email.trim()
    ) {
      setError("First name, last name, and email are required");
      return;
    }

    try {
      setLoading(true);
      const token = localStorage.getItem("token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};

      await axios.post(`${API_URL}/users/invite`, inviteData, { headers });
      setSuccess("Invitation sent successfully");
      handleCloseInviteModal();
      await fetchUsers(); // Refresh users list
    } catch (err) {
      const errorMessage =
        err.response?.data?.error?.message ||
        err.response?.data?.error ||
        "Failed to send invitation";
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleCloseInviteModal = () => {
    setShowInviteModal(false);
    setInviteData({ firstName: "", lastName: "", email: "" });
    setInviteStep("email");
    setExistingUser(null);
    setError("");
  };

  // Role management functions
  const handlePromoteToCommissioner = (userData) => {
    setSelectedUserForCommissioner(userData);
    setCommissionerData({ leagueId: "" });
    setShowCommissionerModal(true);
  };

  const handleAssignCommissioner = async () => {
    if (!commissionerData.leagueId) {
      setError("Please select a league");
      return;
    }

    try {
      setLoading(true);
      setError("");
      setSuccess("");
      const token = localStorage.getItem("token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};

      await axios.post(
        `${API_URL}/leagues/${commissionerData.leagueId}/assign-commissioner`,
        {
          userId: selectedUserForCommissioner.id,
        },
        { headers },
      );

      setSuccess(
        `${selectedUserForCommissioner.name} assigned as commissioner successfully`,
      );
      setShowCommissionerModal(false);
      setSelectedUserForCommissioner(null);
      setCommissionerData({ leagueId: "" });
      await fetchUsers(); // Refresh users list
    } catch (err) {
      const errorMessage =
        err.response?.data?.error?.message ||
        err.response?.data?.error ||
        "Failed to assign commissioner";
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveCommissioner = async (userId) => {
    try {
      setLoading(true);
      setError("");
      setSuccess("");
      const token = localStorage.getItem("token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};

      await axios.delete(`${API_URL}/users/${userId}/commissioner`, {
        headers,
      });
      setSuccess("Commissioner rights removed successfully");
      await fetchUsers(); // Refresh users list
    } catch (err) {
      const errorMessage =
        err.response?.data?.error?.message ||
        err.response?.data?.error ||
        "Failed to remove commissioner rights";
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveAdmin = async (userId) => {
    if (
      !confirm("Are you sure you want to remove admin rights from this user?")
    ) {
      return;
    }

    try {
      setLoading(true);
      setError("");
      setSuccess("");
      const token = localStorage.getItem("token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};

      await axios.delete(`${API_URL}/users/${userId}/admin`, { headers });
      setSuccess("Admin rights removed successfully");
      await fetchUsers(); // Refresh users list
    } catch (err) {
      const errorMessage =
        err.response?.data?.error?.message ||
        err.response?.data?.error ||
        "Failed to remove admin rights";
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleApproveUser = async (userId) => {
    try {
      setLoading(true);
      setError("");
      setSuccess("");
      const token = localStorage.getItem("token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};

      await axios.post(`${API_URL}/users/${userId}/approve`, {}, { headers });
      setSuccess("User approved successfully");
      await fetchUsers(); // Refresh users list
    } catch (err) {
      const errorMessage =
        err.response?.data?.error?.message ||
        err.response?.data?.error ||
        "Failed to approve user";
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteLeague = async (leagueId, leagueName) => {
    if (
      !confirm(
        `Are you sure you want to delete the league "${leagueName}"? This action cannot be undone.`,
      )
    ) {
      return;
    }

    try {
      setLoading(true);
      setError("");
      setSuccess("");
      const token = localStorage.getItem("token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};

      await axios.delete(`${API_URL}/leagues/${leagueId}`, { headers });
      setSuccess("League deleted successfully");
      await fetchLeagues(); // Refresh leagues list
    } catch (err) {
      const errorMessage =
        err.response?.data?.error?.message ||
        err.response?.data?.error ||
        "Failed to delete league";
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // New admin action handlers
  const handleResetPassword = async (userId, newPassword) => {
    try {
      const token = localStorage.getItem("token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};

      await axios.patch(
        `${API_URL}/users/${userId}/reset-password`,
        {
          newPassword,
        },
        { headers },
      );

      setSuccess("Password reset successfully");
      return Promise.resolve();
    } catch (err) {
      const errorMessage =
        err.response?.data?.error?.message ||
        err.response?.data?.error ||
        "Failed to reset password";
      setError(errorMessage);
      return Promise.reject(err);
    }
  };

  const handleRemoveFromLeague = async (userId, leagueId) => {
    try {
      const token = localStorage.getItem("token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};

      await axios.patch(
        `${API_URL}/users/${userId}/remove-from-league`,
        {
          leagueId,
        },
        { headers },
      );

      setSuccess("User removed from league successfully");
      return Promise.resolve();
    } catch (err) {
      const errorMessage =
        err.response?.data?.error?.message ||
        err.response?.data?.error ||
        "Failed to remove user from league";
      setError(errorMessage);
      return Promise.reject(err);
    }
  };

  const handleRemoveFromAllLeagues = async (userId) => {
    if (
      !confirm(
        "Are you sure you want to remove this user from ALL leagues? This action cannot be undone.",
      )
    ) {
      return;
    }

    try {
      setLoading(true);
      const token = localStorage.getItem("token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};

      await axios.patch(
        `${API_URL}/users/${userId}/remove-from-all-leagues`,
        {},
        { headers },
      );
      setSuccess("User removed from all leagues successfully");
      await fetchUsers(); // Refresh users list
    } catch (err) {
      const errorMessage =
        err.response?.data?.error?.message ||
        err.response?.data?.error ||
        "Failed to remove user from all leagues";
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleEditPicks = async (userId, week, picks) => {
    try {
      const token = localStorage.getItem("token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};

      await axios.patch(
        `${API_URL}/users/${userId}/edit-picks`,
        {
          week,
          picks,
        },
        { headers },
      );

      setSuccess("Picks updated successfully");
      return Promise.resolve();
    } catch (err) {
      const errorMessage =
        err.response?.data?.error?.message ||
        err.response?.data?.error ||
        "Failed to update picks";
      setError(errorMessage);
      return Promise.reject(err);
    }
  };

  const handleAssignCommissionerToLeague = async (userId, leagueId) => {
    try {
      const token = localStorage.getItem("token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};

      await axios.post(
        `${API_URL}/leagues/${leagueId}/assign-commissioner`,
        {
          userId,
        },
        { headers },
      );

      setSuccess("Commissioner assigned successfully");
      await fetchUsers(); // Refresh users list
      return Promise.resolve();
    } catch (err) {
      const errorMessage =
        err.response?.data?.error?.message ||
        err.response?.data?.error ||
        "Failed to assign commissioner";
      setError(errorMessage);
      return Promise.reject(err);
    }
  };

  const handleAddToLeague = async (userId, leagueId) => {
    try {
      const token = localStorage.getItem("token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};

      await axios.patch(
        `${API_URL}/users/${userId}/add-to-league`,
        {
          leagueId,
        },
        { headers },
      );

      setSuccess("User added to league successfully");
      await fetchUsers(); // Refresh users list
      return Promise.resolve();
    } catch (err) {
      const errorMessage =
        err.response?.data?.error?.message ||
        err.response?.data?.error ||
        "Failed to add user to league";
      setError(errorMessage);
      return Promise.reject(err);
    }
  };

  if (!user?.isAdmin) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="text-6xl mb-4">🚫</div>
          <h2 className="text-xl font-bold text-gray-900 mb-2">
            Access Denied
          </h2>
          <p className="text-gray-600">
            You need administrator privileges to access this page.
          </p>
        </div>
      </div>
    );
  }

  const tabs = [
    { id: "users", label: "User Management", icon: <FiUsers size={16} /> },
    {
      id: "leagues",
      label: "League Management",
      icon: <FiSettings size={16} />,
    },
    { id: "games", label: "Game Management", icon: <FiEdit size={16} /> },
    { id: "email-templates", label: "Email Templates", icon: <FiMail size={16} /> },
  ];

  return (
    <div className={`${isMobile ? "h-full flex flex-col" : ""}`}>
      {/* Header */}
      <div className="mb-6">
        <h1
          className={`${isMobile ? "text-xl" : "text-2xl"} font-bold text-center`}
        >
          Admin Panel
        </h1>
        <p className="text-gray-600 text-center mt-1">
          Manage users and leagues
        </p>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-gray-200 mb-6">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => {
              setActiveTab(tab.id);
              // Update URL parameters
              const newParams = new URLSearchParams(searchParams);
              newParams.set("tab", tab.id);
              setSearchParams(newParams);
            }}
            className={`flex-1 py-2 px-4 text-sm font-medium border-b-2 transition-colors ${
              activeTab === tab.id
                ? "border-blue-500 text-blue-600"
                : "border-transparent text-gray-500 hover:text-gray-700"
            }`}
          >
            <div className="flex items-center justify-center gap-2">
              {tab.icon}
              <span className={isMobile ? "hidden" : ""}>{tab.label}</span>
            </div>
          </button>
        ))}
      </div>

      {/* Alerts */}
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}
      {success && (
        <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-4">
          {success}
        </div>
      )}

      {/* Tab Content */}
      <div
        className={`${isMobile ? "flex-1 overflow-y-auto" : ""}`}
        style={{ overflow: "visible" }}
      >
        {activeTab === "users" && (
          <div className="space-y-6" style={{ overflow: "visible" }}>
            {/* Users Table */}
            <div
              className="bg-white rounded-lg border border-gray-200 shadow-sm"
              style={{ overflow: "visible" }}
            >
              <div className="p-6 border-b border-gray-200 flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold">All Users</h3>
                  <p className="text-sm text-gray-600 mt-1">
                    Manage user roles and league assignments
                  </p>
                </div>
                <Button
                  onClick={() => setShowInviteModal(true)}
                  className="flex items-center gap-2"
                >
                  <FiPlus size={16} />
                  Invite User
                </Button>
              </div>
              <AdminTableWrapper>
                <div
                  className="overflow-x-auto"
                  style={{ overflowY: "visible" }}
                >
                  <table className="w-full relative">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          User
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Status
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Roles
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-40">
                          Last Login
                        </th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {allUsers.map((userData) => (
                        <tr key={userData.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div>
                              <div className="text-sm font-medium text-gray-900">
                                {userData.name || userData.email}
                              </div>
                              <div className="text-sm text-gray-500">
                                {userData.email}
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            {userData.isApproved ? (
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                Approved
                              </span>
                            ) : (
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                                Pending
                              </span>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex gap-1">
                              {userData.isAdmin && (
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                  Admin
                                </span>
                              )}
                              {userData.isCommissioner && (
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                  Commissioner
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">
                              {userData.lastLoginAt
                                ? formatDateTime(userData.lastLoginAt)
                                : "Never"}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium relative">
                            <ActionsMenu
                              disabled={loading}
                              actions={[
                                // Approve user action
                                ...(!userData.isApproved
                                  ? [
                                      {
                                        label: "Approve User",
                                        icon: FiCheck,
                                        onClick: () =>
                                          handleApproveUser(userData.id),
                                      },
                                    ]
                                  : []),
                                // Commissioner actions
                                ...(!userData.isCommissioner
                                  ? [
                                      {
                                        label: "Make Commissioner",
                                        icon: FiShield,
                                        onClick: () =>
                                          handlePromoteToCommissioner(userData),
                                      },
                                    ]
                                  : [
                                      {
                                        label: "Remove Commissioner",
                                        icon: FiUserMinus,
                                        onClick: () =>
                                          handleRemoveCommissioner(userData.id),
                                      },
                                      {
                                        label: "Assign to Another League",
                                        icon: FiShield,
                                        onClick: () => {
                                          setSelectedUser(userData);
                                          setShowAssignCommissionerModal(true);
                                        },
                                      },
                                    ]),
                                // Admin actions (can't remove yourself)
                                ...(userData.isAdmin && userData.id !== user?.id
                                  ? [
                                      {
                                        label: "Remove Admin",
                                        icon: FiUserMinus,
                                        variant: "danger",
                                        onClick: () =>
                                          handleRemoveAdmin(userData.id),
                                      },
                                    ]
                                  : []),
                                // Additional admin actions
                                {
                                  label: "Reset Password",
                                  icon: FiKey,
                                  onClick: () => {
                                    setSelectedUser(userData);
                                    setShowResetPasswordModal(true);
                                  },
                                },
                                {
                                  label: "Add to League",
                                  icon: FiPlus,
                                  onClick: () => {
                                    setSelectedUser(userData);
                                    setShowAddToLeagueModal(true);
                                  },
                                },
                                {
                                  label: "Remove from League",
                                  icon: FiUserMinus,
                                  onClick: () => {
                                    setSelectedUser(userData);
                                    setShowRemoveFromLeagueModal(true);
                                  },
                                },
                                {
                                  label: "Delete from\\ All Leagues",
                                  icon: FiTrash2,
                                  variant: "danger",
                                  onClick: () =>
                                    handleRemoveFromAllLeagues(userData.id),
                                },
                                {
                                  label: "Edit Picks",
                                  icon: FiEdit,
                                  onClick: () => {
                                    setSelectedUser(userData);
                                    setShowEditPicksModal(true);
                                  },
                                },
                              ]}
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </AdminTableWrapper>
            </div>
          </div>
        )}

        {/* Game Management Tab */}
        {activeTab === "games" && <AdminGameManagement />}

        {activeTab === "leagues" && (
          <div className="space-y-6" style={{ overflow: "visible" }}>
            {/* Leagues Table */}
            <div
              className="bg-white rounded-lg border border-gray-200 shadow-sm"
              style={{ overflow: "visible" }}
            >
              <div className="p-6 border-b border-gray-200 flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold">All Leagues</h3>
                  <p className="text-sm text-gray-600 mt-1">
                    Manage leagues and their settings
                  </p>
                </div>
                <Button
                  onClick={() => setShowLeagueModal(true)}
                  className="flex items-center gap-2"
                >
                  <FiPlus size={16} />
                  Start New League
                </Button>
              </div>
              <AdminTableWrapper>
                <div
                  className="overflow-x-auto"
                  style={{ overflowY: "visible" }}
                >
                  <table className="w-full relative">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          League
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Members
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Commissioner
                        </th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {leagues.map((league) => (
                        <tr key={league.value} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div>
                              <div className="text-sm font-medium text-gray-900">
                                {league.label}
                              </div>
                              {league.description && (
                                <div className="text-sm text-gray-500">
                                  {league.description}
                                </div>
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                              {league.memberCount || 0} members
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">
                              {league.commissionerName || "No Commissioner"}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                            <div className="flex justify-end gap-2">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() =>
                                  navigate(
                                    `/league-management?league=${league.value}`,
                                  )
                                }
                                disabled={loading}
                              >
                                <FiEdit size={14} className="mr-1" />
                                Manage
                              </Button>
                              <Button
                                size="sm"
                                variant="danger"
                                onClick={() =>
                                  handleDeleteLeague(league.value, league.label)
                                }
                                disabled={loading}
                              >
                                <FiTrash2 size={14} />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </AdminTableWrapper>
            </div>
          </div>
        )}

        {/* Email Templates Tab */}
        {activeTab === "email-templates" && (
          <div className="space-y-6" style={{ overflow: "visible" }}>
            <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6">
              <div className="text-center">
                <h3 className="text-lg font-semibold mb-2">Email Template Testing</h3>
                <p className="text-sm text-gray-600 mb-4">
                  Test and preview email templates with mock data
                </p>
                <Button
                  onClick={() => navigate("/admin/email-templates")}
                  className="flex items-center gap-2"
                >
                  <FiMail size={16} />
                  Open Email Template Tool
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Invite User Modal */}
      <Modal
        isOpen={showInviteModal}
        onClose={handleCloseInviteModal}
        title={
          inviteStep === "email"
            ? "Check User Email"
            : inviteStep === "existing"
              ? "User Already Exists"
              : "Invite New User"
        }
        size="md"
      >
        <div className="space-y-4">
          {inviteStep === "email" && (
            <>
              <Input
                label="Email Address"
                type="email"
                value={inviteData.email}
                onChange={(e) =>
                  setInviteData((prev) => ({ ...prev, email: e.target.value }))
                }
                placeholder="Enter user's email address..."
                required
              />
              <div className="flex gap-3 pt-4">
                <Button
                  variant="outline"
                  onClick={handleCloseInviteModal}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleCheckEmail}
                  disabled={loading || !inviteData.email.trim()}
                  className="flex-1"
                >
                  {loading ? "Checking..." : "Check Email"}
                </Button>
              </div>
            </>
          )}

          {inviteStep === "existing" && existingUser && (
            <>
              <div className="p-4 bg-blue-50 rounded-lg">
                <h4 className="font-medium text-blue-900">
                  User Already Exists
                </h4>
                <p className="text-sm text-blue-700 mt-1">
                  <strong>{existingUser.name}</strong> ({existingUser.email}) is
                  already registered.
                </p>
                <p className="text-sm text-blue-600 mt-2">
                  You can add them to a league using the &quot;Add to
                  League&quot; action in the user management table.
                </p>
              </div>
              <div className="flex gap-3 pt-4">
                <Button
                  variant="outline"
                  onClick={() => setInviteStep("email")}
                  className="flex-1"
                >
                  Try Different Email
                </Button>
                <Button onClick={handleCloseInviteModal} className="flex-1">
                  Close
                </Button>
              </div>
            </>
          )}

          {inviteStep === "details" && (
            <>
              <div className="p-4 bg-green-50 rounded-lg">
                <h4 className="font-medium text-green-900">New User</h4>
                <p className="text-sm text-green-700 mt-1">
                  {inviteData.email} is not registered. Please provide their
                  name to send an invitation.
                </p>
              </div>
              <Input
                label="First Name"
                value={inviteData.firstName}
                onChange={(e) =>
                  setInviteData((prev) => ({
                    ...prev,
                    firstName: e.target.value,
                  }))
                }
                placeholder="Enter user's first name..."
                required
              />
              <Input
                label="Last Name"
                value={inviteData.lastName}
                onChange={(e) =>
                  setInviteData((prev) => ({
                    ...prev,
                    lastName: e.target.value,
                  }))
                }
                placeholder="Enter user's last name..."
                required
              />
              <div className="flex gap-3 pt-4">
                <Button
                  variant="outline"
                  onClick={() => setInviteStep("email")}
                  className="flex-1"
                >
                  Back
                </Button>
                <Button
                  onClick={handleInviteUser}
                  disabled={
                    loading ||
                    !inviteData.firstName.trim() ||
                    !inviteData.lastName.trim()
                  }
                  className="flex-1"
                >
                  {loading ? "Sending..." : "Send Invitation"}
                </Button>
              </div>
            </>
          )}
        </div>
      </Modal>

      {/* Create League Modal */}
      <Modal
        isOpen={showLeagueModal}
        onClose={() => {
          setShowLeagueModal(false);
          setNewLeague({ name: "", description: "", isPaidLeague: false });
        }}
        title="Start New League"
        size="md"
      >
        <div className="space-y-4">
          <Input
            label="League Name"
            value={newLeague.name}
            onChange={(e) =>
              setNewLeague((prev) => ({ ...prev, name: e.target.value }))
            }
            placeholder="Enter league name..."
            required
          />
          <Input
            label="Description (Optional)"
            value={newLeague.description}
            onChange={(e) =>
              setNewLeague((prev) => ({ ...prev, description: e.target.value }))
            }
            placeholder="Enter league description..."
          />

          {/* Paid League Toggle */}
          <div className="flex items-center space-x-3">
            <input
              type="checkbox"
              id="isPaidLeague"
              checked={newLeague.isPaidLeague}
              onChange={(e) =>
                setNewLeague((prev) => ({
                  ...prev,
                  isPaidLeague: e.target.checked,
                }))
              }
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            />
            <label
              htmlFor="isPaidLeague"
              className="text-sm font-medium text-gray-700"
            >
              Paid League
            </label>
          </div>

          <div className="flex gap-3 pt-4">
            <Button
              variant="outline"
              onClick={() => {
                setShowLeagueModal(false);
                setNewLeague({
                  name: "",
                  description: "",
                  isPaidLeague: false,
                });
              }}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              onClick={handleCreateLeague}
              disabled={loading || !newLeague.name.trim()}
              className="flex-1"
            >
              {loading ? "Creating..." : "Create League"}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Assign Commissioner Modal */}
      <Modal
        isOpen={showCommissionerModal}
        onClose={() => {
          setShowCommissionerModal(false);
          setSelectedUserForCommissioner(null);
          setCommissionerData({ leagueId: "" });
        }}
        title="Assign Commissioner"
        size="md"
      >
        <div className="space-y-4">
          <div className="p-4 bg-gray-50 rounded-lg">
            <h4 className="font-medium">Selected User:</h4>
            <p className="text-sm text-gray-600">
              {selectedUserForCommissioner?.name} (
              {selectedUserForCommissioner?.email})
            </p>
          </div>

          <Dropdown
            label="Select League"
            value={commissionerData.leagueId}
            onChange={(value) => setCommissionerData({ leagueId: value })}
            options={leagues}
            placeholder="Choose a league..."
          />

          <div className="flex gap-3 pt-4">
            <Button
              variant="outline"
              onClick={() => {
                setShowCommissionerModal(false);
                setSelectedUserForCommissioner(null);
                setCommissionerData({ leagueId: "" });
              }}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              onClick={handleAssignCommissioner}
              disabled={loading || !commissionerData.leagueId}
              className="flex-1"
            >
              {loading ? "Assigning..." : "Assign Commissioner"}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Reset Password Modal */}
      <ResetPasswordModal
        isOpen={showResetPasswordModal}
        onClose={() => {
          setShowResetPasswordModal(false);
          setSelectedUser(null);
        }}
        user={selectedUser}
        onResetPassword={handleResetPassword}
      />

      {/* Remove from League Modal */}
      <RemoveFromLeagueModal
        isOpen={showRemoveFromLeagueModal}
        onClose={() => {
          setShowRemoveFromLeagueModal(false);
          setSelectedUser(null);
        }}
        user={selectedUser}
        onRemoveFromLeague={handleRemoveFromLeague}
      />

      {/* Edit Picks Modal */}
      <EditPicksModal
        isOpen={showEditPicksModal}
        onClose={() => {
          setShowEditPicksModal(false);
          setSelectedUser(null);
        }}
        user={selectedUser}
        onEditPicks={handleEditPicks}
      />

      {/* Assign Commissioner Modal */}
      <AssignCommissionerModal
        isOpen={showAssignCommissionerModal}
        onClose={() => {
          setShowAssignCommissionerModal(false);
          setSelectedUser(null);
        }}
        user={selectedUser}
        onAssignCommissioner={handleAssignCommissionerToLeague}
      />

      {/* Add to League Modal */}
      <AddToLeagueModal
        isOpen={showAddToLeagueModal}
        onClose={() => {
          setShowAddToLeagueModal(false);
          setSelectedUser(null);
        }}
        user={selectedUser}
        onAddToLeague={handleAddToLeague}
      />
    </div>
  );
};

export default AdminPanel;
